import React from 'react';
import { Marker as LeafletMarker, Popup } from 'react-leaflet';
import L, { LeafletEvent } from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T } from '@tolgee/react';
import { LatLng, ReverseGeoMarker } from '../Map.interface';
import RedPin from '../../../assets/RedPin';
import TickIcon from '../../../assets/TickIcon';

interface ReverseGeoPinProps {
	marker: ReverseGeoMarker;
	onDragEnd: (pos: LatLng) => Promise<void>;
	onConfirm: (e: React.MouseEvent) => void;
	target?: string;
	handleMarkerDrag?: (e: LeafletEvent) => void;
	currentMarkerPosition?: LatLng;
	showConfirm?: boolean;
}

const ReverseGeoPin: React.FC<ReverseGeoPinProps> = ({
	marker,
	onDragEnd,
	onConfirm,
	target,
	currentMarkerPosition,
	handleMarkerDrag,
	showConfirm = true,
}) => {
	const { id, position, locationName } = marker;

	const icon = React.useMemo(
		() =>
			L.divIcon({
				html: ReactDOMServer.renderToString(
					<div className="flex flex-col items-center">
						<div className="relative">
							<RedPin />
						</div>
					</div>,
				),
				className: 'custom-user-marker',
				iconSize: [42, 42],
				iconAnchor: [21, 42],
			}),
		[],
	);

	if (!currentMarkerPosition) return null;

	return (
		<LeafletMarker
			key={id}
			position={[currentMarkerPosition!.lat, currentMarkerPosition!.lng]}
			icon={icon}
			draggable
			eventHandlers={{
				drag: handleMarkerDrag,
				dragend: async (e) => {
					const { lat, lng } = e.target.getLatLng();
					await onDragEnd({ lat, lng });
					e.target.openPopup();
				},
				add: (e) => {
					if (locationName) {
						setTimeout(() => {
							e.target.openPopup();
						}, 300);
					}
				},
			}}
		>
			{locationName ? (
				<Popup
					offset={[0, -44]}
					autoClose={false}
					closeOnClick={false}
					autoPan={false}
					keepInView={false}
				>
					<div className="flex flex-col gap-3 text-left">
						<SypacText variant="body-regular-small">
							<p className="text-[20px] leading-[32px] text-gray-80 !m-0">
								{locationName}
							</p>
						</SypacText>

						<SypacText variant="overline-regular-small">
							<p className="text-[14px] leading-[20px] text-gray-40 !m-0">
								Lat: {position.lat.toFixed(6)}, Lng: {position.lng.toFixed(6)}
							</p>
						</SypacText>

						{showConfirm && target !== 'transporter' ? (
							<SypacButton
								variant="secondary"
								size="small"
								className="w-full mt-2"
							>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										onConfirm(e);
									}}
									className="w-full h-[32px] flex gap-3 items-center justify-center bg-primary-violet border-0 rounded-lg p-0 cursor-pointer transition hover:bg-primary-violet/80"
								>
									<TickIcon />
									<SypacText variant="body-regular-medium">
										<p className="text-white">
											<T keyName="map.confirm">Confirm</T>
										</p>
									</SypacText>
								</button>
							</SypacButton>
						) : null}
					</div>
				</Popup>
			) : null}
		</LeafletMarker>
	);
};

export default ReverseGeoPin;
